import { Language } from '@yleisradio/areena-types';
import { useEffect, useRef } from 'react';
import { Notification } from '../Notifications/types';
import {
  addConnectionStateListener as addTizenConnectionStateListener,
  removeConnectionStateListener as removeTizenConnectionStateListener,
  ListenerId as TizenListenerId,
} from '../tizen';
import { tizenMode } from '../../common/types';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useNotificationContext } from '../contexts/NotificationContext/NotificationContext';

const networkDisconnectedId = 'NETWORK_DISCONNECTED';

const createNotification = (language: Language): Notification => {
  return {
    id: networkDisconnectedId,
    message:
      language === 'sv' ? `Ingen internetanslutning` : `Ei internetyhteyttä`,
    hideAutomatically: false,
  };
};

export const useOfflineNotification = () => {
  const { languageCode, mode } = useAppFeaturesContext();
  const tizenNetworkListenerId = useRef<TizenListenerId | undefined>(undefined);
  const {
    pushNotification: showNotification,
    removeNotification: hideNotification,
  } = useNotificationContext();

  const networkDisconnectedNotification = createNotification(languageCode);

  const connectionStateListener = (isConnected: boolean) => {
    if (isConnected) {
      hideNotification(networkDisconnectedNotification);
    } else {
      showNotification(networkDisconnectedNotification);
    }
  };

  const offlineListener = () => {
    showNotification(networkDisconnectedNotification);
  };

  const onlineListener = () => {
    hideNotification(networkDisconnectedNotification);
  };

  useEffect(() => {
    switch (mode) {
      case tizenMode: {
        const listenerId = addTizenConnectionStateListener(
          connectionStateListener
        );

        if (listenerId) {
          tizenNetworkListenerId.current = listenerId;
          break;
        }
      }
      default: {
        window.addEventListener('offline', offlineListener);
        window.addEventListener('online', onlineListener);
      }
    }

    return () => {
      if (tizenNetworkListenerId.current) {
        removeTizenConnectionStateListener(tizenNetworkListenerId.current);
      }
      window.removeEventListener('offline', offlineListener);
      window.removeEventListener('online', onlineListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // returned for testing purposes
  return { connectionStateListener };
};
